import { logError } from "@/lib/lazySentry";
import { isInAppBrowser } from ".";

export const handleFetchSVG = async (url) => {
  return fetch(url)
    .then((res) => {
      if (!res.ok) {
        // throw new Error("Network response was not ok");
      }
      return res.text();
    })
    .then((svgText) => {
      return svgText;
    });
};

export const convertHeicToPng = async (file) => {
  const heicConverter = (await import("heic-convert/browser")).default;

  const result = await heicConverter({
    buffer: new Uint8Array(await file.arrayBuffer()),
    format: "PNG",
  });

  const pngBlob = new Blob([result], { type: "image/png" });
  return new File([pngBlob], file.name.replace(/\.[^/.]+$/, ".png"), {
    type: "image/png",
    lastModified: Date.now(),
  });
};

export const convertAvifToPng = async (avifFile) => {
  const avifDataURL = await fileToDataURL(avifFile);

  const image = await loadImage(avifDataURL);

  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, 0, 0);

  const pngDataURL = canvas.toDataURL("image/png");

  const pngBlob = dataURLToBlob(pngDataURL);

  return new File([pngBlob], avifFile.name.replace(/\.[^/.]+$/, ".png"), {
    type: "image/png",
    lastModified: Date.now(),
  });
};

// Helper: Convert File object to Data URL
function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const loadImage = (dataURL) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataURL;
  });
};

const dataURLToBlob = (dataURL) => {
  const [header, base64] = dataURL.split(",");
  const mime = header.match(/:(.*?);/)[1];
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: mime });
};

export const handleConvertImage = async ({ image, mirror = false, forceProxy = false }) => {
  const canvas = document.createElement("canvas");
  let img = new Image();
  img.crossOrigin = "anonymous";

  if (!image.startsWith("data:image") && (isInAppBrowser() || forceProxy)) {
    img.src = `${process.env.NEXT_PUBLIC_API_BASE_URL}/v2/proxy-image/?url=${encodeURIComponent(
      image
    )}`;
  } else {
    const response = await fetch(image, { mode: "cors" });
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    img.src = blobUrl;
  }

  try {
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = (err) => {
        if (forceProxy) {
          logError(
            new Error(`We encountered an error while converting the image: ${err?.message}`)
          );
        }
        reject(err);
      };
    });
  } catch (err) {
    console.error(err);
    if (!forceProxy) return await handleConvertImage({ image, mirror, forceProxy: true });
    throw new Error("Error on loading image");
  }

  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");

  if (mirror) {
    ctx.save();
    ctx.scale(-1, 1);
    ctx.drawImage(img, 0, 0, img.naturalWidth * -1, img.naturalHeight);
    ctx.restore();
  } else {
    ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
  }

  return canvas.toDataURL("image/png");
};

export const downloadFile = (fileName, url) => {
  const link = document.createElement("a");
  link.download = fileName;
  link.href = url;
  link.click();
};
export const resizeToDataURL = ({ resizeMinimalDimension, img, callback }) => {
  const image = new Image();

  image.src = img;
  // image.crossOrigin = "anonymous";
  image.onload = () => {
    const canvas = document.createElement("canvas");

    const smallerSize = Math.min(image.naturalWidth, image.naturalHeight);
    let resize = 1;

    if (smallerSize < resizeMinimalDimension) resize = resizeMinimalDimension / smallerSize;

    canvas.width = resize * image.naturalWidth;
    canvas.height = resize * image.naturalHeight;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "transparent";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const dataUrl = canvas.toDataURL("image/png");
    callback(dataUrl, resize);
  };
};

export async function svgFileToPngBlob(file, fixedWidth, fixedHeight) {
  if (!file.type.includes("svg")) {
    console.error("Selected file is not an SVG.");
    return;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = function (error) {
      logError(new Error(`Error on converting image ${file.type}: ${error?.message}`));
      reject(error);
    };
    reader.onload = function (event) {
      const svgDataUrl = event.target.result;

      const img = new Image();
      img.onload = function () {
        let { width, height } = img;
        const aspectRatio = width / height;

        // Scale image to fit within fixedWidth x fixedHeight while maintaining aspect ratio
        let newWidth = fixedWidth;
        let newHeight = fixedHeight;

        if (aspectRatio > 1) {
          // Image is wider than tall
          newHeight = fixedWidth / aspectRatio;
          if (newHeight > fixedHeight) {
            newHeight = fixedHeight;
            newWidth = fixedHeight * aspectRatio;
          }
        } else {
          // Image is taller than wide
          newWidth = fixedHeight * aspectRatio;
          if (newWidth > fixedWidth) {
            newWidth = fixedWidth;
            newHeight = fixedWidth / aspectRatio;
          }
        }

        // Create a fixed-size canvas
        const canvas = document.createElement("canvas");
        canvas.width = fixedWidth;
        canvas.height = fixedHeight;
        const ctx = canvas.getContext("2d");

        // Center the image on the fixed-size canvas
        const offsetX = (fixedWidth - newWidth) / 2;
        const offsetY = (fixedHeight - newHeight) / 2;
        ctx.drawImage(img, offsetX, offsetY, newWidth, newHeight);

        // Convert canvas to PNG Blob
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          }
        }, "image/png");
      };
      img.src = svgDataUrl;
    };

    reader.readAsDataURL(file);
  });
}
